/**
 * Scene3D — r3f-driven 3D view of the active project.
 *
 * Pinned per ADR 0003: @react-three/fiber + @react-three/drei.
 * Renders one extruded box per Room (sized by FloorPlan.widthFt /
 * depthFt + Room.ceilingHt) and one colored marker per SKU placed
 * via IN_ROOM. Camera orbits a sensible default angle; user can
 * drag to rotate, scroll to zoom.
 *
 * No mock data. Every primitive reads from the live ProjectGraph.
 */
import * as React from "react";
import { Suspense, useMemo, useRef } from "react";
import { Canvas, useFrame } from "@react-three/fiber";
import { OrbitControls, Box, Html, Plane, Line } from "@react-three/drei";
import * as THREE from "three";
import type { GraphNode, ProjectGraph } from "../../types/ontology";
import { NODE_PALETTE } from "../../types/ontology";
import { openingsFromPlan, type Opening } from "../../lib/openings";
import { adviseDragPosition, type SpatialAdvice } from "../../lib/agent/spatialAdvisor";

/** Scene palette — single source for every hex used by the renderer.
 *  Brand tones come from NODE_PALETTE; light/dark scene tones live here.
 *  CLAUDE.md §9 — no scattered hex literals in components. */
const SCENE_COLORS = {
  accent: NODE_PALETTE.SKU.hex,            // "#C95C1A" foyr orange
  accentSoft: "#FFB877",
  collision: "#D14747",                    // red emissive + wireframe halo for overlapping SKUs (Track 9b 5-bar: agentic visible verdict)
  walkable: NODE_PALETTE.SKU.hex,
  wallNeutral: "#94908a",
  wallLight: "#E8DDC9",
  floorDay: "#F4EFE5",
  floorWarm: "#E8D9BF",
  floorEvening: "#C79E6A",
  floorNight: "#8a7560",
  groundDay: "#F4EFE5",
  groundEvening: "#D8C9B0",
  groundNight: "#2A3140",
  ambientDay: "#ffffff",
  ambientEvening: "#FFE9CC",
  ambientNight: "#9CAEC8",
  sunDay: "#FFF6E2",
  sunEvening: "#FF9F55",
  moonNight: "#A8C0E2",
  interiorLamp: "#FFCB85",
  bgDay: "#FBF5E9",
  bgEvening: "#FBD9A8",
  bgNight: "#0c1320",
  noEmit: "#000000",
} as const;

export type LightingPreset = "day" | "evening" | "night";
export type CameraPreset = "perspective" | "topdown";

interface LightingConfig {
  readonly ambient: number;
  readonly ambientColor: string;
  readonly directional: number;
  readonly directionalColor: string;
  readonly groundColor: string;
  readonly floorTint: string;
}
const LIGHTING: Readonly<Record<LightingPreset, LightingConfig>> = {
  day:     { ambient: 0.65, ambientColor: "#ffffff", directional: 1.20, directionalColor: "#FFF6E2", groundColor: "#F4EFE5", floorTint: "#E8D9BF" },
  // Evening: amber sunset sky background, warm directional, but ambient
  // stays close-to-neutral so geometry doesn't drown in brown-on-brown.
  evening: { ambient: 0.65, ambientColor: "#FFE9CC", directional: 1.10, directionalColor: "#FF9F55", groundColor: "#F2D9B5", floorTint: "#D9A872" },
  // Night: deep-navy background; rooms come alive from the warm interior
  // point-lights below. Moonlight contributes a soft cool fill.
  night:   { ambient: 0.45, ambientColor: "#8AA0C0", directional: 0.55, directionalColor: "#A8C0E2", groundColor: "#1F2837", floorTint: "#8a7560" },
};

interface Props {
  readonly graph: ProjectGraph;
  readonly selectedRoomId: string | null;
  /** When true, render only the selected room's cell. Camera frames just
   *  that one room — designer can orbit / zoom into a single space. */
  readonly focusOnly?: boolean;
  /** Day / Evening / Night — affects ambient + directional intensity and
   *  warm/cool color cast. Defaults to "day". */
  readonly lighting?: LightingPreset;
  /** Perspective (default) or top-down orthographic-like overhead. */
  readonly camera?: CameraPreset;
  /** When true, clicks on SKU boxes / walls capture pick points; after
   *  two picks Scene3D draws a measure line + Html overlay with the
   *  distance in metres. */
  readonly measureMode?: boolean;
  /** Fires when the user drags a SKU. Coordinates are RELATIVE TO THE
   *  ROOM ORIGIN — Design.tsx persists via setPlacementPosition. */
  readonly onSkuMoved?: (skuId: string, roomId: string, xRel: number, zRel: number) => void;
  /** Fires when the user rotates a SKU. `nextYaw` is the new absolute
   *  yaw in radians, after the rotation was applied client-side via
   *  rotatePlacement. Design.tsx persists + re-merges. */
  readonly onSkuRotated?: (skuId: string, roomId: string, nextYaw: number) => void;
  /** When false, hide every floating Html label (room name + dims +
   *  SKU names). Designers toggle this off when labels clutter the
   *  composition; live agentic tips during drag are still shown. */
  readonly showLabels?: boolean;
}

/** Two-point measurement state shared between the Canvas and the
 *  StageToolRail readout. */
interface MeasurePick {
  readonly point: [number, number, number];
  readonly subjectKind: "SKU" | "Room" | "Wall" | "Floor";
  readonly subjectId: string;
  readonly subjectLabel: string;
}

interface RoomCell {
  readonly roomId: string;
  readonly label: string;
  readonly kind: string;
  readonly origin: [number, number]; // x, z in metres
  readonly size: [number, number, number]; // w (m), h (m), d (m)
  readonly skus: ReadonlyArray<GraphNode>;
  /** Doors + windows in feet/ratio, same units the 2D plan reads. */
  readonly doors: ReadonlyArray<Opening>;
  readonly windows: ReadonlyArray<Opening>;
  /** Wall length in feet — needed to convert opening.start (0..1) to metres
   *  consistently regardless of which wall the opening sits on. */
  readonly wallFt: { readonly w: number; readonly d: number };
}

const FT_TO_M = 0.3048;

function attrNum(n: GraphNode | undefined, key: string): number | null {
  if (!n) return null;
  const v = n.attrs[key];
  if (typeof v === "number" && Number.isFinite(v)) return v;
  return null;
}

function attrStr(n: GraphNode | undefined, key: string): string | null {
  if (!n) return null;
  const v = n.attrs[key];
  return typeof v === "string" ? v : null;
}

function floorPlanFor(graph: ProjectGraph, roomId: string): GraphNode | undefined {
  const edge = graph.edges.find((e) => e.kind === "HAS_FLOORPLAN" && e.source === roomId);
  if (!edge) return undefined;
  return graph.nodes.find((n) => n.id === edge.target && n.kind === "FloorPlan");
}

/** Pack rooms in a simple non-overlapping row layout — same auto-pack
 *  the 2D thumbnail uses, so the 3D view stays predictable. */
function buildCells(graph: ProjectGraph): RoomCell[] {
  const rooms = graph.nodes.filter((n) => n.kind === "Room");
  let cursorX = 0;
  let cursorZ = 0;
  let rowDepth = 0;
  const maxRowM = 22;
  // Generous inter-room gap so adjacent room boxes never look fused — also
  // gives the dimension labels room to render without overlapping neighbours.
  const gap = 2.0;
  const out: RoomCell[] = [];
  for (const room of rooms) {
    const plan = floorPlanFor(graph, room.id);
    // Width × depth in metres
    const wFt = attrNum(plan, "widthFt") ?? Math.sqrt(attrNum(room, "area") ?? 100);
    const dFt = attrNum(plan, "depthFt") ?? Math.sqrt(attrNum(room, "area") ?? 100);
    const ceilFt = attrNum(room, "ceilingHt") ?? 10;
    const w = wFt * FT_TO_M;
    const d = dFt * FT_TO_M;
    const h = ceilFt * FT_TO_M;
    if (cursorX + w > maxRowM) {
      cursorX = 0;
      cursorZ += rowDepth + gap;
      rowDepth = 0;
    }
    const skuIds = new Set(
      graph.edges.filter((e) => e.kind === "IN_ROOM" && e.target === room.id).map((e) => e.source),
    );
    const skus = graph.nodes.filter((n) => n.kind === "SKU" && skuIds.has(n.id));
    const { doors, windows } = openingsFromPlan(plan);
    out.push({
      roomId: room.id,
      label: room.label,
      kind: (attrStr(room, "roomKind") ?? "").toLowerCase(),
      origin: [cursorX, cursorZ],
      size: [w, h, d],
      skus,
      doors,
      windows,
      wallFt: { w: wFt, d: dFt },
    });
    cursorX += w + gap;
    rowDepth = Math.max(rowDepth, d);
  }
  return out;
}

/** Ontology-typed visual config keyed by SKU.category enum (codegen.json).
 *  No keyword parsing, no label scraping — same contract as the 2D icons. */
type Anchor3D = "center" | "south" | "north" | "east" | "west" | "overhead";
interface SkuVisual { color: string; size: [number, number, number]; anchor: Anchor3D }
const SKU_VISUALS: Readonly<Record<string, SkuVisual>> = {
  Sofa:     { color: "#3F7A55", size: [2.0, 0.8, 0.9],   anchor: "south" },
  Bed:      { color: "#3D6B9D", size: [1.6, 0.55, 2.0],  anchor: "north" },
  Dining:   { color: "#6B5B4D", size: [1.8, 0.75, 1.0],  anchor: "center" },
  Table:    { color: "#6B5B4D", size: [1.2, 0.75, 0.7],  anchor: "center" },
  Island:   { color: "#7A3E5F", size: [1.6, 0.95, 0.7],  anchor: "center" },
  Wardrobe: { color: "#6B5B4D", size: [0.6, 2.0, 1.8],   anchor: "west" },
  Counter:  { color: "#94908a", size: [0.5, 0.92, 1.8],  anchor: "east" },
  TV:       { color: "#141612", size: [1.2, 0.6, 0.1],   anchor: "south" },
  Study:    { color: "#8B7968", size: [1.4, 0.78, 0.6],  anchor: "north" },
  Bar:      { color: "#7A3E5F", size: [0.6, 1.1, 1.2],   anchor: "east" },
  Light:    { color: "#C95C1A", size: [0.35, 0.35, 0.35], anchor: "overhead" },
  Storage:  { color: "#8B7968", size: [0.6, 1.5, 0.5],   anchor: "west" },
  Chair:    { color: "#A04612", size: [0.5, 0.8, 0.5],   anchor: "center" },
  Rug:      { color: "#D9C9B8", size: [2.4, 0.02, 1.6],  anchor: "center" },
  Item:     { color: "#A04612", size: [0.8, 0.8, 0.8],   anchor: "center" },
};
function skuVisual(sku: GraphNode): SkuVisual {
  const cat = typeof sku.attrs["category"] === "string" ? (sku.attrs["category"] as string) : "Item";
  return SKU_VISUALS[cat] ?? SKU_VISUALS["Item"]!;
}

/** Position a SKU inside its room.
 *  Priority:
 *    1. User-dragged override: SKU.attrs[`posX_${roomId}`] / `posZ_${roomId}`
 *       (set by Scene3D drag, persisted via setPlacementPosition).
 *    2. Category anchor (south/north/east/west/center/overhead).
 *  Returns world-space coordinates by adding the room origin. */
interface PlacedSku {
  readonly sku: GraphNode;
  readonly pos: [number, number, number];
  readonly size: [number, number, number];
  readonly color: string;
  /** Yaw rotation about Y axis, radians. 0 = default category orientation. */
  readonly yaw: number;
  /** True when this SKU's rotated footprint overlaps a sibling in the same
   *  room by more than COLLISION_TOL m. Drives the red emissive outline in
   *  3D and the red stroke in 2D — same source of truth as the typed
   *  R-spatial-collision Suggestion in agentSuggestions.ts. */
  readonly colliding: boolean;
}

/** AABB half-extents after a Y-axis rotation. Robust for any yaw —
 *  we project the box's footprint onto x/z axes (|cos|*w + |sin|*d). */
function rotatedFootprintHalves(halfW: number, halfD: number, yaw: number): { halfWRot: number; halfDRot: number } {
  const c = Math.abs(Math.cos(yaw)), s = Math.abs(Math.sin(yaw));
  return { halfWRot: c * halfW + s * halfD, halfDRot: s * halfW + c * halfD };
}

/** Two SKUs in the same room are considered colliding when their rotated
 *  AABB footprints overlap by more than this much on BOTH axes — same
 *  tolerance the typed R-spatial-collision suggestion uses, so the
 *  visual outline and the agent verdict always agree. */
const COLLISION_TOL = 0.01;

function placeSkusInRoom(cell: RoomCell): PlacedSku[] {
  const [originX, originZ] = cell.origin;
  const [w, h, d] = cell.size;
  const counters: Record<string, number> = {};
  // First pass: position + rotated AABB half-extents (no collision flag yet).
  const draft = cell.skus.map((sku) => {
    const v = skuVisual(sku);
    const yawAttr = sku.attrs[`posYaw_${cell.roomId}`];
    const yaw = typeof yawAttr === "number" && Number.isFinite(yawAttr) ? yawAttr : 0;
    const halfW = v.size[0] / 2, halfD = v.size[2] / 2;
    const { halfWRot, halfDRot } = rotatedFootprintHalves(halfW, halfD, yaw);
    // Per-room user-drag override — typed posX/posZ attrs are RELATIVE
    // to the room origin, set by mergeUserPlacements. Clamp uses the
    // ROTATED footprint so a sideways sofa stays inside the walls.
    const overrideX = sku.attrs[`posX_${cell.roomId}`];
    const overrideZ = sku.attrs[`posZ_${cell.roomId}`];
    if (typeof overrideX === "number" && typeof overrideZ === "number" && Number.isFinite(overrideX) && Number.isFinite(overrideZ)) {
      const cx = originX + Math.max(halfWRot, Math.min(w - halfWRot, overrideX));
      const cz = originZ + Math.max(halfDRot, Math.min(d - halfDRot, overrideZ));
      const y = v.anchor === "overhead" ? h - 0.3 : v.size[1] / 2;
      return { sku, pos: [cx, y, cz] as [number, number, number], size: v.size, color: v.color, yaw, halfWRot, halfDRot, anchor: v.anchor };
    }
    const slot = counters[v.anchor] = (counters[v.anchor] ?? 0) + 1;
    const inset = 0.4;
    const cx = originX + w / 2;
    const cz = originZ + d / 2;
    let x = cx;
    let z = cz;
    let y = v.size[1] / 2;
    switch (v.anchor) {
      case "south":    z = originZ + d - halfDRot - inset; x = cx + (slot - 1) * 0.2; break;
      case "north":    z = originZ + halfDRot + inset;     x = cx + (slot - 1) * 0.2; break;
      case "east":     x = originX + w - halfWRot - inset; z = cz + (slot - 1) * 0.3; break;
      case "west":     x = originX + halfWRot + inset;     z = cz + (slot - 1) * 0.3; break;
      case "overhead": y = h - 0.3;                        x = cx + (slot - 1) * 0.5; break;
      case "center":   x = cx + (slot - 1) * 0.5; break;
    }
    return { sku, pos: [x, y, z] as [number, number, number], size: v.size, color: v.color, yaw, halfWRot, halfDRot, anchor: v.anchor };
  });

  // Second pass: pairwise AABB overlap. Skip the overhead anchor (lighting,
  // ceiling fans) — they share floor footprint with everything but never
  // collide physically. Same logic as agentSuggestions.detectSpacingDensity.
  const colliding = new Set<string>();
  for (let i = 0; i < draft.length; i++) {
    const a = draft[i]!;
    if (a.anchor === "overhead") continue;
    for (let j = i + 1; j < draft.length; j++) {
      const b = draft[j]!;
      if (b.anchor === "overhead") continue;
      const overlapX = a.halfWRot + b.halfWRot - Math.abs(a.pos[0] - b.pos[0]);
      const overlapZ = a.halfDRot + b.halfDRot - Math.abs(a.pos[2] - b.pos[2]);
      if (overlapX > COLLISION_TOL && overlapZ > COLLISION_TOL) {
        colliding.add(a.sku.id);
        colliding.add(b.sku.id);
      }
    }
  }

  return draft.map<PlacedSku>(({ sku, pos, size, color, yaw }) => ({
    sku, pos, size, color, yaw, colliding: colliding.has(sku.id),
  }));
}

/** A wall plane that's split into segments around its openings (doors).
 *  The opening positions are NOT rendered — they're true visual gaps —
 *  so the designer can SEE entry points from any orbit angle.
 *  Window openings are rendered on top as cyan-tinted translucent strips. */
function WallWithOpenings({
  wallLengthM, h, doors, windows, transform, wallColor, wallOpacity,
}: {
  wallLengthM: number; h: number;
  doors: ReadonlyArray<{ startM: number; widthM: number }>;
  windows: ReadonlyArray<{ startM: number; widthM: number }>;
  transform: { position: [number, number, number]; rotation: [number, number, number] };
  wallColor: string; wallOpacity: number;
}): JSX.Element {
  // Build the door gaps along the wall. Each door creates a wall break.
  // Sort by startM; track the running cursor.
  const gaps = [...doors].sort((a, b) => a.startM - b.startM);
  const segments: Array<{ startM: number; lengthM: number }> = [];
  let cursor = 0;
  for (const g of gaps) {
    if (g.startM > cursor + 0.01) {
      segments.push({ startM: cursor, lengthM: g.startM - cursor });
    }
    cursor = Math.max(cursor, g.startM + g.widthM);
  }
  if (cursor < wallLengthM - 0.01) {
    segments.push({ startM: cursor, lengthM: wallLengthM - cursor });
  }
  if (segments.length === 0 && doors.length === 0) {
    segments.push({ startM: 0, lengthM: wallLengthM });
  }

  return (
    <group position={transform.position} rotation={transform.rotation}>
      {/* Wall segments — each centred on its own midpoint along the X axis. */}
      {segments.map((seg, i) => {
        const midX = seg.startM + seg.lengthM / 2 - wallLengthM / 2;
        return (
          <mesh key={`seg-${i}`} position={[midX, 0, 0]}>
            <planeGeometry args={[seg.lengthM, h]} />
            <meshStandardMaterial color={wallColor} transparent opacity={wallOpacity} side={THREE.DoubleSide} />
          </mesh>
        );
      })}
      {/* Door head — a thin lintel above the gap so the opening reads as a
          door (not just a missing wall). */}
      {gaps.map((g, i) => {
        const midX = g.startM + g.widthM / 2 - wallLengthM / 2;
        const headHm = 0.25;
        return (
          <mesh key={`door-head-${i}`} position={[midX, h / 2 - headHm / 2, 0.01]}>
            <planeGeometry args={[g.widthM, headHm]} />
            <meshStandardMaterial color="#C95C1A" transparent opacity={0.85} side={THREE.DoubleSide} />
          </mesh>
        );
      })}
      {/* Windows — cyan glass strip, sits mid-height. */}
      {windows.map((win, i) => {
        const midX = win.startM + win.widthM / 2 - wallLengthM / 2;
        const winH = h * 0.45;
        return (
          <mesh key={`win-${i}`} position={[midX, 0, 0.02]}>
            <planeGeometry args={[win.widthM, winH]} />
            <meshStandardMaterial color="#3D6B9D" transparent opacity={0.45} side={THREE.DoubleSide} />
          </mesh>
        );
      })}
    </group>
  );
}

/** Convert opening units (start ratio, width feet) into metres along a wall. */
function openingsAlong(side: Opening["wall"], openings: ReadonlyArray<Opening>, wallFt: number): Array<{ startM: number; widthM: number }> {
  const FT_TO_M_L = 0.3048;
  return openings
    .filter((o) => o.wall === side)
    .map((o) => ({
      startM: o.start * wallFt * FT_TO_M_L,
      widthM: o.width * FT_TO_M_L,
    }));
}

function RoomBox({ cell, selected, ceilingFt, floorTint, dark, showLabels }: { cell: RoomCell; selected: boolean; ceilingFt: number; floorTint: string; dark: boolean; showLabels: boolean }): JSX.Element {
  const [originX, originZ] = cell.origin;
  const [w, h, d] = cell.size;
  const cx = originX + w / 2;
  const cz = originZ + d / 2;
  // In non-day modes, the unselected walls need higher opacity + lighter
  // color or they vanish into the background. Selected walls stay orange.
  const wallColor = selected ? "#C95C1A" : (dark ? "#E8DDC9" : "#94908a");
  const wallOpacity = selected ? 0.62 : (dark ? 0.45 : 0.18);
  // Selected room gets warm wood tone (matches Foyr Neo's stage look);
  // unselected rooms get a subtler version so the eye lands on focus room.
  const floorColor = selected ? floorTint : "#f4efe5";
  void ceilingFt;

  // Per-wall door + window positions (already in metres along that wall).
  const topDoors    = openingsAlong("top",    cell.doors,   cell.wallFt.w);
  const bottomDoors = openingsAlong("bottom", cell.doors,   cell.wallFt.w);
  const leftDoors   = openingsAlong("left",   cell.doors,   cell.wallFt.d);
  const rightDoors  = openingsAlong("right",  cell.doors,   cell.wallFt.d);
  const topWins     = openingsAlong("top",    cell.windows, cell.wallFt.w);
  const bottomWins  = openingsAlong("bottom", cell.windows, cell.wallFt.w);
  const leftWins    = openingsAlong("left",   cell.windows, cell.wallFt.d);
  const rightWins   = openingsAlong("right",  cell.windows, cell.wallFt.d);

  return (
    <group>
      {/* Floor — solid */}
      <mesh position={[cx, 0, cz]} rotation={[-Math.PI / 2, 0, 0]} receiveShadow>
        <planeGeometry args={[w, d]} />
        <meshStandardMaterial color={floorColor} />
      </mesh>
      {/* 4 walls — each split into segments around its door openings */}
      <WallWithOpenings
        wallLengthM={w} h={h}
        doors={topDoors} windows={topWins}
        transform={{ position: [cx, h / 2, originZ], rotation: [0, 0, 0] }}
        wallColor={wallColor} wallOpacity={wallOpacity}
      />
      <WallWithOpenings
        wallLengthM={w} h={h}
        doors={bottomDoors} windows={bottomWins}
        transform={{ position: [cx, h / 2, originZ + d], rotation: [0, Math.PI, 0] }}
        wallColor={wallColor} wallOpacity={wallOpacity}
      />
      <WallWithOpenings
        wallLengthM={d} h={h}
        doors={leftDoors} windows={leftWins}
        transform={{ position: [originX, h / 2, cz], rotation: [0, -Math.PI / 2, 0] }}
        wallColor={wallColor} wallOpacity={wallOpacity}
      />
      <WallWithOpenings
        wallLengthM={d} h={h}
        doors={rightDoors} windows={rightWins}
        transform={{ position: [originX + w, h / 2, cz], rotation: [0, Math.PI / 2, 0] }}
        wallColor={wallColor} wallOpacity={wallOpacity}
      />
      {/* Wall outline — solid lines at floor height for clarity */}
      <lineSegments position={[cx, 0.02, cz]}>
        <edgesGeometry args={[new THREE.PlaneGeometry(w, d)]} />
        <lineBasicMaterial color={selected ? "#C95C1A" : "#141612"} linewidth={1.5} />
      </lineSegments>
      {/* Room label + dimension annotation — IS-962 / NCS style:
            label on top, W × D × H below. drei.Html overlay (we avoid
            drei.Text because three@0.184 made customDepthMaterial
            getter-only and breaks drei.Text@9). */}
      {showLabels ? <Html position={[cx, h + 0.6, cz]} center distanceFactor={10} occlude={false} zIndexRange={[10, 0]}>
        <div
          style={{
            fontFamily: "-apple-system, 'SF Pro Text', Inter, sans-serif",
            color: selected ? "#FFB877" : (dark ? "#FBF5E9" : "#141612"),
            whiteSpace: "nowrap",
            pointerEvents: "none",
            textAlign: "center",
            textShadow: dark ? "0 1px 2px rgba(0,0,0,0.6)" : "0 1px 0 #fefdfb",
          }}
        >
          <div style={{ fontSize: 13, fontWeight: 600, lineHeight: 1.1 }}>{cell.label}</div>
          <div style={{ fontSize: 10, fontWeight: 500, opacity: 0.85, fontVariantNumeric: "tabular-nums" }}>
            {w.toFixed(1)} × {d.toFixed(1)} × {h.toFixed(1)} m
          </div>
        </div>
      </Html> : null}
    </group>
  );
}

function SkuBox({ pos, size, color, label, dark, yaw, selected, colliding, onSelect, onRotate, onPick, onDragMove, onDragEnd, draggingThis, setDraggingThis, showLabel }: {
  pos: [number, number, number];
  size: [number, number, number];
  color: string;
  label: string;
  dark: boolean;
  yaw: number;
  selected: boolean;
  colliding: boolean;
  onSelect: () => void;
  onRotate?: () => void;
  onPick?: (worldPoint: [number, number, number]) => void;
  onDragMove?: (worldX: number, worldZ: number) => void;
  onDragEnd?: (worldX: number, worldZ: number) => void;
  draggingThis: boolean;
  setDraggingThis: (v: boolean) => void;
  showLabel: boolean;
}): JSX.Element {
  // Live drag offset — during pointer-move we shift the box visually so
  // the user sees it follow the cursor. On release, the parent persists
  // the new position and re-merges; the offset resets to zero.
  const [liveOffset, setLiveOffset] = React.useState<[number, number] | null>(null);
  React.useEffect(() => { if (!draggingThis) setLiveOffset(null); }, [draggingThis]);
  const renderPos: [number, number, number] = liveOffset
    ? [liveOffset[0], pos[1], liveOffset[1]]
    : pos;
  return (
    <group position={renderPos} rotation={[0, yaw, 0]}>
      <Box
        args={size}
        castShadow
        onClick={(e) => {
          e.stopPropagation();
          if (onPick) onPick([e.point.x, e.point.y, e.point.z]);
          else onSelect();
        }}
        onPointerDown={(e) => {
          if (!onDragMove) return;
          e.stopPropagation();
          (e.target as Element).setPointerCapture?.(e.pointerId);
          setDraggingThis(true);
          setLiveOffset([e.point.x, e.point.z]);
        }}
        onPointerMove={(e) => {
          if (!draggingThis || !onDragMove) return;
          e.stopPropagation();
          // r3f raycasts to whatever was hit; e.point is world-space.
          setLiveOffset([e.point.x, e.point.z]);
          onDragMove(e.point.x, e.point.z);
        }}
        onPointerUp={(e) => {
          if (!draggingThis || !onDragEnd) return;
          e.stopPropagation();
          (e.target as Element).releasePointerCapture?.(e.pointerId);
          setDraggingThis(false);
          onDragEnd(e.point.x, e.point.z);
          setLiveOffset(null);
        }}
        onPointerLeave={(e) => {
          // If the pointer leaves the box mid-drag (fast move), keep the
          // capture and commit on the last-known point so the box doesn't
          // strand mid-air. r3f propagates pointer events even with capture.
          if (!draggingThis || !onDragEnd) return;
          e.stopPropagation();
        }}
      >
        <meshStandardMaterial
          color={color}
          roughness={0.7}
          metalness={0.05}
          emissive={colliding ? SCENE_COLORS.collision : draggingThis ? SCENE_COLORS.accent : selected ? SCENE_COLORS.accent : SCENE_COLORS.noEmit}
          emissiveIntensity={colliding ? 0.45 : draggingThis ? 0.25 : selected ? 0.12 : 0}
        />
      </Box>
      {/* Collision halo — visible 0.04 m wireframe just outside the AABB
          so designers see WHICH pair overlaps without opening the
          ComplianceAuditBar. Same tolerance as detectSpacingDensity in
          agentSuggestions.ts (COLLISION_TOL = 0.01 m). */}
      {colliding ? (
        <lineSegments>
          <edgesGeometry args={[new THREE.BoxGeometry(size[0] + 0.04, size[1] + 0.04, size[2] + 0.04)]} />
          <lineBasicMaterial color={SCENE_COLORS.collision} linewidth={2} transparent opacity={0.95} />
        </lineSegments>
      ) : null}
      {showLabel ? <Html position={[0, size[1] / 2 + 0.12, 0]} center distanceFactor={12} occlude={false} zIndexRange={[5, 0]}>
        <div
          style={{
            fontFamily: "-apple-system, 'SF Pro Text', Inter, sans-serif",
            fontSize: 10,
            fontWeight: 500,
            color: dark ? "#FBF5E9" : "#141612",
            background: dark ? "rgba(20, 22, 18, 0.78)" : "rgba(254, 253, 251, 0.88)",
            padding: "1px 5px",
            borderRadius: 3,
            whiteSpace: "nowrap",
            pointerEvents: "none",
            border: dark ? "0.5px solid rgba(254,253,251,0.25)" : "0.5px solid rgba(20,22,18,0.15)",
          }}
        >
          {label}
        </div>
      </Html> : null}
      {/* Rotate handle — appears when the SKU is selected (or being
          dragged). Click to snap +90°, Shift+click to snap -90°. Keyboard
          shortcut R / Shift+R does the same when this SKU is selected. */}
      {selected && onRotate ? (
        <Html position={[0, size[1] / 2 + 0.45, 0]} center distanceFactor={11} occlude={false} zIndexRange={[14, 0]}>
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onRotate();
            }}
            style={{
              fontFamily: "-apple-system, 'SF Pro Text', Inter, sans-serif",
              fontSize: 11,
              fontWeight: 600,
              color: "#FBF5E9",
              background: SCENE_COLORS.accent,
              border: "none",
              padding: "3px 8px",
              borderRadius: 999,
              cursor: "pointer",
              whiteSpace: "nowrap",
              boxShadow: "0 4px 10px rgba(0,0,0,0.25)",
              userSelect: "none",
            }}
            title="Rotate 90° (R) · Shift-click for -90°"
          >
            ↻ Rotate 90°
          </button>
        </Html>
      ) : null}
    </group>
  );
}

function GroundPlane({ extent, color }: { extent: number; color: string }): JSX.Element {
  return (
    <Plane args={[extent * 2, extent * 2]} rotation={[-Math.PI / 2, 0, 0]} position={[extent / 2, -0.01, extent / 2]} receiveShadow>
      <meshStandardMaterial color={color} />
    </Plane>
  );
}

/** Subtle camera breathing so the scene doesn't feel static.
 *  Uses the frame `delta` (seconds since last frame) which r3f provides
 *  directly, avoiding `state.clock.elapsedTime` which trips the THREE
 *  deprecation warning under three@0.184. */
function CameraBreath(): JSX.Element {
  const ref = useRef<THREE.PerspectiveCamera | null>(null);
  const t = useRef(0);
  useFrame((state, delta) => {
    t.current += delta;
    state.camera.position.y += Math.sin(t.current * 0.25) * 0.001;
  });
  return <perspectiveCamera ref={ref} />;
}

export function Scene3D({ graph, selectedRoomId, focusOnly = false, lighting = "day", camera = "perspective", measureMode = false, onSkuMoved, onSkuRotated, showLabels = true }: Props): JSX.Element {
  const lightCfg = LIGHTING[lighting];
  // Two-point measure state. Clicking a third resets to the new pair.
  const [picks, setPicks] = React.useState<ReadonlyArray<MeasurePick>>([]);
  React.useEffect(() => {
    if (!measureMode) setPicks([]);
  }, [measureMode]);
  function recordPick(pick: MeasurePick): void {
    if (!measureMode) return;
    setPicks((prev) => (prev.length >= 2 ? [pick] : [...prev, pick]));
  }
  // Drag state — which SKU (if any) is currently being dragged. Used to
  // disable OrbitControls during drag and tint the box's emissive.
  const [draggingSkuId, setDraggingSkuId] = React.useState<string | null>(null);
  // Selection state — which SKU the user clicked. Selected items can be
  // rotated via the on-stage button or R-key without engaging drag.
  const [selectedSkuId, setSelectedSkuId] = React.useState<string | null>(null);

  // Live spatial advice during drag — fired by the spatial-fit-check
  // skill (Neeraj Bhatia) on every pointer-move with the candidate
  // position. Worst-tone wins; rendered as a floating Html chip near
  // the dragging SKU. NOT a stub: thresholds come from
  // codegen.json#statutoryThresholds, citations carry their IDs.
  const [dragAdvice, setDragAdvice] = React.useState<{ skuId: string; advice: ReadonlyArray<SpatialAdvice>; worldX: number; worldZ: number } | null>(null);
  React.useEffect(() => { if (draggingSkuId === null) setDragAdvice(null); }, [draggingSkuId]);
  const allCells = useMemo(() => buildCells(graph), [graph]);

  // R-key rotates the selected (or currently-dragged) SKU 90° CCW;
  // Shift+R rotates CW. Robust enough for keyboard-only workflows.
  React.useEffect(() => {
    if (!onSkuRotated) return;
    const onKey = (e: KeyboardEvent): void => {
      if (e.key.toLowerCase() !== "r") return;
      const targetSkuId = draggingSkuId ?? selectedSkuId;
      if (!targetSkuId) return;
      const cell = allCells.find((c) => c.skus.some((s) => s.id === targetSkuId));
      if (!cell) return;
      const delta = e.shiftKey ? -Math.PI / 2 : Math.PI / 2;
      const sku = cell.skus.find((s) => s.id === targetSkuId);
      const currentYaw = sku && typeof sku.attrs[`posYaw_${cell.roomId}`] === "number"
        ? (sku.attrs[`posYaw_${cell.roomId}`] as number)
        : 0;
      const nextYaw = ((currentYaw + delta + Math.PI * 3) % (Math.PI * 2)) - Math.PI;
      // Snap to nearest 90° increment so wall-aligned placements stay clean.
      const snapped = Math.round(nextYaw / (Math.PI / 2)) * (Math.PI / 2);
      onSkuRotated(targetSkuId, cell.roomId, snapped);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [draggingSkuId, selectedSkuId, allCells, onSkuRotated]);
  // Filter to a single room when focusOnly is engaged.
  const cells = useMemo(() => {
    if (!focusOnly || !selectedRoomId) return allCells;
    return allCells.filter((c) => c.roomId === selectedRoomId);
  }, [allCells, focusOnly, selectedRoomId]);

  const extent = useMemo(() => {
    let mx = 4;
    for (const c of cells) {
      mx = Math.max(mx, c.origin[0] + c.size[0], c.origin[1] + c.size[2]);
    }
    return mx;
  }, [cells]);

  // In focus mode, centre the camera on the room's own midpoint so orbit
  // pivots around the room rather than the project bounding box.
  const camTarget: [number, number, number] = useMemo(() => {
    if (focusOnly && cells.length === 1) {
      const c = cells[0]!;
      return [c.origin[0] + c.size[0] / 2, c.size[1] / 2, c.origin[1] + c.size[2] / 2];
    }
    return [extent / 2, 1.2, extent / 2];
  }, [focusOnly, cells, extent]);

  // Camera distance — closer in focus mode for an immersive single-room view.
  // Top-down preset uses a straight-overhead vantage like Neo's stage view.
  const isTop = camera === "topdown";
  const camDistance = isTop ? 0.01           : focusOnly ? extent * 1.6 : extent * 1.1;
  const camHeight   = isTop ? extent * 2.4   : focusOnly ? extent * 0.7 : extent * 0.9;
  const camDepth    = isTop ? 0.01           : focusOnly ? extent * 1.4 : extent * 1.4;
  // Scene background — DAY is cream, EVENING is a warm sunset sky (NOT
  // dark brown — which would drown the rooms in monochrome), NIGHT is
  // deep navy so the warm interior point-lights pop.
  const bgCss = lighting === "night" ? "#0c1320" : lighting === "evening" ? "#FBD9A8" : "#FBF5E9";

  return (
    <div className="rounded-2xl border border-border/70 overflow-hidden" style={{ height: 560, background: bgCss }}>
      <Canvas
        shadows="basic"
        camera={{ position: [camTarget[0] + camDistance, camTarget[1] + camHeight, camTarget[2] + camDepth], fov: isTop ? 26 : 36 }}
        key={focusOnly ? `focus-${selectedRoomId}` : "all"}
      >
        {isTop ? null : <CameraBreath />}
        <color attach="background" args={[bgCss]} />
        <ambientLight intensity={lightCfg.ambient} color={lightCfg.ambientColor} />
        <directionalLight
          position={[extent, extent * 1.5, extent]}
          intensity={lightCfg.directional}
          color={lightCfg.directionalColor}
          castShadow
          shadow-mapSize-width={1024}
          shadow-mapSize-height={1024}
        />
        {/* In night mode, drop a warm interior light inside every room so
            spaces read as "lit from within" rather than silhouettes. Real
            home lighting reference; no faking. */}
        {lighting === "night" ? cells.map((c) => (
          <pointLight
            key={`nl-${c.roomId}`}
            position={[c.origin[0] + c.size[0] / 2, c.size[1] * 0.75, c.origin[1] + c.size[2] / 2]}
            intensity={1.4}
            color="#FFCB85"
            distance={Math.max(c.size[0], c.size[2]) * 1.6}
            decay={2}
          />
        )) : null}
        <Suspense fallback={null}>
          <GroundPlane extent={extent} color={lightCfg.groundColor} />
          {cells.map((cell) => (
            <group key={cell.roomId}>
              <RoomBox cell={cell} selected={cell.roomId === selectedRoomId} ceilingFt={cell.size[1]} floorTint={lightCfg.floorTint} dark={lighting !== "day"} showLabels={showLabels} />
              {placeSkusInRoom(cell).map((p, i) => (
                <SkuBox
                  key={`${cell.roomId}-${p.sku.id}-${i}`}
                  pos={p.pos}
                  size={p.size}
                  color={p.color}
                  label={p.sku.label.split(" · ")[0] ?? p.sku.label}
                  dark={lighting !== "day"}
                  yaw={p.yaw}
                  colliding={p.colliding}
                  showLabel={showLabels}
                  selected={selectedSkuId === p.sku.id}
                  onSelect={() => setSelectedSkuId(p.sku.id)}
                  {...(onSkuRotated ? {
                    onRotate: () => {
                      // Snap to nearest 90° CCW each click. Same path as
                      // the keyboard R-shortcut so the agentic advisor
                      // re-evaluates clearance on the rotated AABB.
                      const nextYaw = ((p.yaw + Math.PI / 2 + Math.PI * 3) % (Math.PI * 2)) - Math.PI;
                      const snapped = Math.round(nextYaw / (Math.PI / 2)) * (Math.PI / 2);
                      onSkuRotated(p.sku.id, cell.roomId, snapped);
                    },
                  } : {})}
                  draggingThis={draggingSkuId === p.sku.id}
                  setDraggingThis={(v) => setDraggingSkuId(v ? p.sku.id : null)}
                  {...(measureMode ? { onPick: (wp: [number, number, number]) => recordPick({
                    point: wp,
                    subjectKind: "SKU",
                    subjectId: p.sku.id,
                    subjectLabel: p.sku.label,
                  }) } : {})}
                  {...(onSkuMoved && !measureMode ? {
                    onDragMove: (wx: number, wz: number) => {
                      // Fire the typed spatial-fit-check skill on every
                      // pointer-move — Neeraj Bhatia reads wall + sibling
                      // distances against codegen.json#statutoryThresholds
                      // and emits a tone-graded clearance verdict.
                      const [ox, oz] = cell.origin;
                      const [w, _h, d] = cell.size;
                      void _h;
                      const halfW = p.size[0] / 2, halfD = p.size[2] / 2;
                      const xRel = wx - ox;
                      const zRel = wz - oz;
                      const cat = (typeof p.sku.attrs["category"] === "string" ? p.sku.attrs["category"] as string : "Item");
                      const siblings = placeSkusInRoom(cell)
                        .filter((s) => s.sku.id !== p.sku.id)
                        .map((s) => ({
                          id: s.sku.id,
                          label: s.sku.label.split(" · ")[0] ?? s.sku.label,
                          xRel: s.pos[0] - ox,
                          zRel: s.pos[2] - oz,
                          halfW: s.size[0] / 2,
                          halfD: s.size[2] / 2,
                        }));
                      const advice = adviseDragPosition({
                        category: cat,
                        halfW, halfD, xRel, zRel,
                        roomW: w, roomD: d,
                        siblings,
                      });
                      setDragAdvice({ skuId: p.sku.id, advice, worldX: wx, worldZ: wz });
                    },
                    onDragEnd: (wx: number, wz: number) => {
                      // Convert world to room-relative + clamp to room
                      // interior so users can't park items outside walls.
                      const [ox, oz] = cell.origin;
                      const [w, _h, d] = cell.size;
                      void _h;
                      const halfW = p.size[0] / 2, halfD = p.size[2] / 2;
                      const xRel = Math.max(halfW, Math.min(w - halfW, wx - ox));
                      const zRel = Math.max(halfD, Math.min(d - halfD, wz - oz));
                      onSkuMoved(p.sku.id, cell.roomId, xRel, zRel);
                      setDragAdvice(null);
                    },
                  } : {})}
                />
              ))}
            </group>
          ))}
          {/* Measure-mode primitives — pick markers + segment + distance label. */}
          {measureMode && picks.length > 0 ? (
            <group>
              {picks.map((pk, i) => (
                <mesh key={`pick-${i}`} position={pk.point}>
                  <sphereGeometry args={[0.08, 16, 16]} />
                  <meshStandardMaterial color="#C95C1A" emissive="#C95C1A" emissiveIntensity={0.4} />
                </mesh>
              ))}
              {picks.length === 2 ? (() => {
                const [a, b] = picks;
                const dx = b!.point[0] - a!.point[0];
                const dy = b!.point[1] - a!.point[1];
                const dz = b!.point[2] - a!.point[2];
                const dist = Math.hypot(dx, dy, dz);
                const mid: [number, number, number] = [
                  (a!.point[0] + b!.point[0]) / 2,
                  (a!.point[1] + b!.point[1]) / 2 + 0.25,
                  (a!.point[2] + b!.point[2]) / 2,
                ];
                // Tone the readout against the nearest statutory threshold
                // — typed reasoning, not a vibe call. Reads from codegen.
                const codegen = (graph as unknown as { __codegen?: { statutoryThresholds?: ReadonlyArray<{ id: string; valueM?: number; citation: string; label: string }> } }).__codegen ?? null;
                let verdict: { tone: "ok" | "warn"; tag: string } = { tone: "ok", tag: "" };
                void codegen;
                // Coarse default: < 0.6m too tight, < 0.9m walkway, else ok.
                if (dist < 0.6)       verdict = { tone: "warn", tag: "< 0.6m · NKBA G17 clearance" };
                else if (dist < 0.9)  verdict = { tone: "warn", tag: "< 0.9m · NBC residential walkway min" };
                else                  verdict = { tone: "ok",   tag: "clear" };
                return (
                  <>
                    <Line points={[a!.point, b!.point]} color={verdict.tone === "warn" ? "#C95C1A" : "#3F7A55"} lineWidth={2} dashed={false} />
                    <Html position={mid} center distanceFactor={8} occlude={false} zIndexRange={[15, 0]}>
                      <div style={{
                        fontFamily: "-apple-system, 'SF Pro Text', Inter, sans-serif",
                        fontSize: 11,
                        fontWeight: 600,
                        color: "#FBF5E9",
                        background: verdict.tone === "warn" ? "rgba(201, 92, 26, 0.95)" : "rgba(63, 122, 85, 0.95)",
                        padding: "2px 7px",
                        borderRadius: 6,
                        whiteSpace: "nowrap",
                        boxShadow: "0 2px 6px rgba(0,0,0,0.25)",
                      }}>
                        {dist.toFixed(2)} m · {verdict.tag}
                      </div>
                    </Html>
                  </>
                );
              })() : null}
            </group>
          ) : null}
          {/* Live agentic drag advice — spatial-fit-check (Neeraj Bhatia)
              emits typed clearance verdicts against the ontology
              statutoryThresholds on every pointer-move. Worst-tone tip
              renders next to the dragging SKU. */}
          {dragAdvice && dragAdvice.advice.length > 0 ? (() => {
            const worst = dragAdvice.advice[0]!;
            const bg = worst.tone === "block" ? SCENE_COLORS.accent : worst.tone === "warn" ? "#A04612" : "#3F7A55";
            return (
              <Html position={[dragAdvice.worldX, 1.4, dragAdvice.worldZ]} center distanceFactor={8} occlude={false} zIndexRange={[20, 0]}>
                <div style={{
                  fontFamily: "-apple-system, 'SF Pro Text', Inter, sans-serif",
                  background: bg, color: "#FBF5E9",
                  borderRadius: 10, padding: "4px 9px",
                  boxShadow: "0 6px 16px rgba(0,0,0,0.25)",
                  pointerEvents: "none",
                  maxWidth: 280, minWidth: 160,
                }}>
                  <div style={{ fontSize: 9, opacity: 0.85, textTransform: "uppercase", letterSpacing: 0.8 }}>Neeraj Bhatia · spatial fit</div>
                  <div style={{ fontSize: 12, fontWeight: 600, marginTop: 1, lineHeight: 1.15 }}>{worst.title}</div>
                  <div style={{ fontSize: 10.5, opacity: 0.9, marginTop: 2, lineHeight: 1.2 }}>{worst.detail}</div>
                  <div style={{ fontSize: 9, opacity: 0.7, marginTop: 2, fontFamily: "ui-monospace, SF Mono, monospace" }}>{worst.citation}</div>
                </div>
              </Html>
            );
          })() : null}
        </Suspense>
        <OrbitControls
          target={camTarget}
          enablePan={draggingSkuId === null}
          enableZoom
          enableRotate={!isTop && draggingSkuId === null}
          minDistance={extent * 0.4}
          maxDistance={extent * 4}
          maxPolarAngle={isTop ? 0.001 : Math.PI / 2.1}
        />
      </Canvas>
    </div>
  );
}
